import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  Upload, Users, UserCheck, TrendingUp, AlertCircle,
  RefreshCw, ArrowRight, BarChart3, Database, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { marketingAPI, leadsAPI } from '../../utils/api';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, sub, onClick }) {
  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-3.5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
          <p className="text-xl font-bold text-slate-900">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-teal-600" />
        </div>
      </div>
    </div>
  );
}

export default function MarketingDashboard() {
  const navigate = useNavigate();
  const [poolStats, setPoolStats] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [recentImports, setRecentImports] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [poolRes, pipeRes, importsRes] = await Promise.allSettled([
        marketingAPI.getLeadPool({ limit: 1 }),
        leadsAPI.getPipelineAnalytics(),
        marketingAPI.getImports(),
      ]);
      if (poolRes.status === 'fulfilled') setPoolStats(poolRes.value.data?.stats || {});
      if (pipeRes.status === 'fulfilled') setPipeline(pipeRes.value.data);
      if (importsRes.status === 'fulfilled') setRecentImports((importsRes.value.data?.imports || []).slice(0, 5));
    } catch {
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const total = (poolStats?.hot || 0) + (poolStats?.warm || 0) + (poolStats?.cold || 0);
  const sc = pipeline?.stage_counts || {};
  const totalLeads = pipeline?.total_leads || 0;
  const convRate = pipeline?.conversion_rate || 0;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
        <div className="p-6 space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Marketing Overview</h1>
              <p className="text-sm text-slate-500">Lead pool, import history, and pipeline analytics</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
              <Button
                onClick={() => navigate('/admin/marketing/upload')}
                className="bg-teal-600 hover:bg-teal-700 gap-1.5"
              >
                <Upload className="w-4 h-4" /> Import Leads
              </Button>
            </div>
          </div>

          {/* KPI Cards */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array(4).fill(0).map((_, i) => (
                <Card key={i} className="animate-pulse"><CardContent className="p-5 h-24" /></Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={Database}
                label="Unassigned Pool"
                value={total}
                sub={`${poolStats?.hot || 0} hot · ${poolStats?.warm || 0} warm`}
                color="bg-teal-500"
                onClick={() => navigate('/admin/marketing/pool')}
              />
              <StatCard
                icon={Users}
                label="Total Leads"
                value={totalLeads}
                sub="all time"
                color="bg-blue-500"
              />
              <StatCard
                icon={UserCheck}
                label="Registered"
                value={sc.registered || 0}
                sub="converted"
                color="bg-emerald-500"
              />
              <StatCard
                icon={TrendingUp}
                label="Conversion Rate"
                value={`${convRate}%`}
                sub="overall"
                color="bg-violet-500"
              />
            </div>
          )}

          {/* Quick actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card
              className="cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border-2 border-dashed border-teal-200 bg-teal-50/50"
              onClick={() => navigate('/admin/marketing/upload')}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                  <Upload className="w-6 h-6 text-teal-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-800">Import Leads</p>
                  <p className="text-sm text-slate-500">Upload CSV with duplicate detection</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              onClick={() => navigate('/admin/marketing/pool')}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <UserCheck className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-800">Lead Pool</p>
                  <p className="text-sm text-slate-500">
                    {total > 0 ? (
                      <span className="text-teal-600 font-semibold">{total} unassigned</span>
                    ) : 'Assign leads to consultants'}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              onClick={() => navigate('/admin/marketing/history')}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-800">Import History</p>
                  <p className="text-sm text-slate-500">
                    {recentImports.length > 0
                      ? `${recentImports.length} recent batches`
                      : 'View all import batches'}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </CardContent>
            </Card>
          </div>

          {/* Pipeline snapshot + Pool breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pipeline */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Pipeline Snapshot</CardTitle>
                  <Button variant="ghost" size="sm" className="gap-1 text-teal-600 text-xs"
                    onClick={() => navigate('/sales/manager/leads')}>
                    View Pipeline <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {[
                  { key: 'new_lead', label: 'New Leads', color: 'bg-blue-500' },
                  { key: 'in_discussion', label: 'In Discussion', color: 'bg-violet-500' },
                  { key: 'prospective', label: 'Prospective', color: 'bg-amber-500' },
                  { key: 'payment_received', label: 'Payment Received', color: 'bg-teal-500' },
                  { key: 'registered', label: 'Registered', color: 'bg-emerald-500' },
                ].map(({ key, label, color }) => {
                  const count = sc[key] || 0;
                  const pct = totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600">{label}</span>
                        <span className="font-semibold text-slate-800">{count} <span className="text-slate-400">({pct}%)</span></span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Pool breakdown */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Pool Breakdown</CardTitle>
                  <Button variant="ghost" size="sm" className="gap-1 text-teal-600 text-xs"
                    onClick={() => navigate('/admin/marketing/pool')}>
                    Manage Pool <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {[
                  { key: 'hot', label: 'Hot', icon: '🔥', color: 'bg-rose-500', textColor: 'text-rose-700', bg: 'bg-rose-50' },
                  { key: 'warm', label: 'Warm', icon: '🌡', color: 'bg-amber-500', textColor: 'text-amber-700', bg: 'bg-amber-50' },
                  { key: 'cold', label: 'Cold', icon: '❄️', color: 'bg-sky-500', textColor: 'text-sky-700', bg: 'bg-sky-50' },
                ].map(({ key, label, icon, color, textColor, bg }) => {
                  const count = poolStats?.[key] || 0;
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={key} className={`flex items-center gap-3 p-3 rounded-xl ${bg}`}>
                      <span className="text-xl">{icon}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={`font-semibold ${textColor}`}>{label}</span>
                          <span className="text-slate-500">{count} leads</span>
                        </div>
                        <div className="h-1.5 bg-white/80 rounded-full overflow-hidden">
                          <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {total === 0 && !loading && (
                  <div className="text-center py-8 text-slate-400">
                    <Target className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No unassigned leads in pool</p>
                    <Button size="sm" className="mt-3 bg-teal-600 hover:bg-teal-700 gap-1.5"
                      onClick={() => navigate('/admin/marketing/upload')}>
                      <Upload className="w-3.5 h-3.5" /> Import Leads
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent imports */}
          {recentImports.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Recent Imports</CardTitle>
                  <Button variant="ghost" size="sm" className="gap-1 text-teal-600 text-xs"
                    onClick={() => navigate('/admin/marketing/history')}>
                    View all <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="divide-y divide-slate-100">
                  {recentImports.map((imp) => (
                    <div key={imp.id} className="flex items-center gap-3 py-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                        <Upload className="w-4 h-4 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{imp.file_name}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(imp.import_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {imp.assigned_to_name && ` · ${imp.assigned_to_name}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">{imp.imported_count} imported</Badge>
                        {imp.duplicate_count > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">{imp.duplicate_count} dup</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}
